import React, { useState } from "react";
import {
  TextField,
  Autocomplete,
  Button,
  IconButton,
  Grid,
  Box,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Formik, Form } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import dayjs from "dayjs";
import { updateTask } from "../../services/taskManagement.service";

const AddSubTask = ({
  setOpenDrawer,
  GetData,
  selectedData,
  subSelectedData,
  userId,
  viewMode,
}) => {
  const VIEW_MODE = "View";
  const EDIT_MODE = "Edit";
  const ADD_MODE = "Add";
  const isViewMode = viewMode === VIEW_MODE;

  const getInitialSubTask = () => {
    if (viewMode === ADD_MODE) {
      return {
        workType: "",
        status: "",
        workDescription: "",
        dateAndTime: [
          { id: 1, date: null, time: { startTime: null, endTime: null } },
        ],
      };
    }

    // Use subSelectedData when in EDIT_MODE or VIEW_MODE
    if (subSelectedData) {
      return {
        ...subSelectedData,
        // Ensure dateAndTime is properly structured
        dateAndTime: subSelectedData.dateAndTime?.map((entry, index) => ({
          id: entry._id || index + 1,
          date: entry.date ? dayjs(entry.date) : null,
          time: {
            startTime: entry.time?.startTime
              ? dayjs(entry.time.startTime)
              : null,
            endTime: entry.time?.endTime ? dayjs(entry.time.endTime) : null,
          },
        })) || [
          { id: 1, date: null, time: { startTime: null, endTime: null } },
        ],
      };
    }

    // Default empty structure
    return {
      workType: "",
      status: "",
      workDescription: "",
      dateAndTime: [
        { id: 1, date: null, time: { startTime: null, endTime: null } },
      ],
    };
  };

  const initialSubTask = getInitialSubTask();

  const [dateAndTime, setDateAndTime] = useState(() => {
    if (initialSubTask.dateAndTime?.length) {
      return initialSubTask.dateAndTime.map((entry, index) => {
        const dateValue = entry.date ? dayjs(entry.date) : null;
        const timeValue = entry.time || {};

        return {
          id: entry._id || `new-${index + 1}`,
          date: dateValue,
          time: {
            startTime: timeValue.startTime ? dayjs(timeValue.startTime) : null,
            endTime: timeValue.endTime ? dayjs(timeValue.endTime) : null,
          },
        };
      });
    }
    return [{ id: 1, date: null, time: { startTime: null, endTime: null } }];
  });

  const [timeErrors, setTimeErrors] = useState({});

  const WORK_TYPE_OPTIONS = ["Development", "Bug", "Testing"];
  const STATUS_OPTIONS = ["To Do", "InProgress", "Completed"];

  const validationSchema = Yup.object({
    workType: Yup.string().required("Work Type is required"),
    status: Yup.string().required("Status is required"),
    workDescription: Yup.string().required("Description is required"),
  });

  const handleAddEntry = () => {
    const newId = Date.now();
    setDateAndTime([
      ...dateAndTime,
      {
        id: newId,
        date: null,
        time: { startTime: null, endTime: null },
      },
    ]);
  };

  const handleRemoveEntry = (id) => {
    if (dateAndTime.length > 1) {
      setDateAndTime(dateAndTime.filter((e) => e.id !== id));
      setTimeErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const validateTimeEntry = (
    entry,
    allEntries = dateAndTime,
    existingSubTasks = []
  ) => {
    const {
      date,
      time: { startTime, endTime },
    } = entry;

    if (!startTime && !endTime) return null;

    const fullDate = dayjs(date).format("YYYY-MM-DD");

    const start = dayjs(`${fullDate}T${dayjs(startTime).format("HH:mm")}`);
    const end = dayjs(`${fullDate}T${dayjs(endTime).format("HH:mm")}`);

    if (start.isSame(end, "minute")) {
      return "Start time and end time cannot be the same";
    }

    if (end.isBefore(start, "minute")) {
      return "End time must be after start time";
    }

    for (let i = 0; i < allEntries.length; i++) {
      const existing = allEntries[i];
      if (existing.id === entry.id) continue; // skip self

      const existingDate = dayjs(existing.date).format("YYYY-MM-DD");
      if (existingDate !== fullDate) continue;

      const existingStart = dayjs(
        `${existingDate}T${dayjs(existing.time.startTime).format("HH:mm")}`
      );
      const existingEnd = dayjs(
        `${existingDate}T${dayjs(existing.time.endTime).format("HH:mm")}`
      );

      if (start.isSame(existingStart, "minute")) {
        return "Start time is matching with existing Start Time";
      }

      if (
        (start.isAfter(existingStart) && start.isBefore(existingEnd)) ||
        (end.isAfter(existingStart) && end.isBefore(existingEnd)) ||
        (start.isBefore(existingStart) && end.isAfter(existingEnd)) ||
        (start.isAfter(existingStart) && end.isBefore(existingEnd))
      ) {
        return "Time entry overlaps with an existing entry";
      }

      // 2. Check overlaps against existing DB subtasks
      if (selectedData?.subTasks?.length) {
        for (const sub of selectedData.subTasks) {
          if (sub._id === subSelectedData?._id) continue; // skip self during edit

          for (const dt of sub.dateAndTime || []) {
            const dbDate = dayjs(dt.date).format("YYYY-MM-DD");
            if (dbDate !== fullDate) continue;

            const dbStart = dayjs(
              `${dbDate}T${dayjs(dt.time.startTime).format("HH:mm")}`
            );
            const dbEnd = dayjs(
              `${dbDate}T${dayjs(dt.time.endTime).format("HH:mm")}`
            );

            if (start.isBefore(dbEnd) && end.isAfter(dbStart)) {
              return "Time entry overlaps with another subtask";
            }
          }
        }
      }
    }

    // Existing subtasks from DB
    for (let i = 0; i < existingSubTasks.length; i++) {
      const existing = existingSubTasks[i];

      if (subSelectedData && existing._id === subSelectedData._id) continue; // skip current editing subtask

      for (let j = 0; j < existing.dateAndTime.length; j++) {
        const dbEntry = existing.dateAndTime[j];
        const existingDate = dayjs(dbEntry.date).format("YYYY-MM-DD");
        if (existingDate !== fullDate) continue;

        const existingStart = dayjs(
          `${existingDate}T${dayjs(dbEntry.time.startTime).format("HH:mm")}`
        );
        const existingEnd = dayjs(
          `${existingDate}T${dayjs(dbEntry.time.endTime).format("HH:mm")}`
        );

        if (
          (start.isAfter(existingStart) && start.isBefore(existingEnd)) ||
          (end.isAfter(existingStart) && end.isBefore(existingEnd)) ||
          (start.isBefore(existingStart) && end.isAfter(existingEnd)) ||
          (start.isAfter(existingStart) && end.isBefore(existingEnd)) ||
          start.isSame(existingStart, "minute")
        ) {
          return "Time entry overlaps with another subtask";
        }
      }
    }

    return null;
  };

  const handleDateTimeChange = (id, field, value) => {
    setDateAndTime((prev) => {
      const updated = prev.map((entry) => {
        if (entry.id === id) {
          return {
            ...entry,
            ...(field === "date"
              ? { date: value }
              : {
                  time: {
                    ...entry.time,
                    [field]: value,
                  },
                }),
          };
        }
        return entry;
      });

      // Validate after full update
      const updatedEntry = updated.find((e) => e.id === id);
      const error = validateTimeEntry(
        updatedEntry,
        updated,
        selectedData?.subTasks || []
      );

      setTimeErrors((prevErrors) => ({
        ...prevErrors,
        [id]: error || undefined,
      }));

      return updated;
    });
  };

  const validateAllTimeEntries = () => {
    const newTimeErrors = {};
    let hasErrors = false;

    dateAndTime.forEach((entry) => {
      const error = validateTimeEntry(
        entry,
        dateAndTime,
        selectedData?.subTasks || []
      );
      if (error) {
        newTimeErrors[entry.id] = error;
        hasErrors = true;
      }
    });

    setTimeErrors(newTimeErrors);
    return !hasErrors;
  };

  const handleSubTaskSubmit = async (values) => {
    // First validate all time entries
    if (!validateAllTimeEntries()) {
      return;
    }

    const formattedDateTime = dateAndTime.map((entry) => {
      const baseObj = {
        date: entry.date ? entry.date.toISOString() : null,
        time: {
          startTime: entry.time.startTime
            ? entry.time.startTime.toISOString()
            : null,
          endTime: entry.time.endTime ? entry.time.endTime.toISOString() : null,
        },
      };

      if (typeof entry.id === "string" && !entry.id.startsWith("new-")) {
        baseObj._id = entry.id;
      }

      return baseObj;
    });

    const subTask = {
      userId,
      workType: values.workType,
      status: values.status,
      workDescription: values.workDescription,
      dateAndTime: formattedDateTime,
    };

    const editSubTask = {
      userId,
      workType: values.workType,
      status: values.status,
      workDescription: values.workDescription,
      dateAndTime: formattedDateTime,
      subTaskId: subSelectedData._id,
    };

    try {
      if (viewMode === EDIT_MODE) {
        await updateTask(selectedData._id, {
          bodyData: JSON.stringify({ subTask: editSubTask }),
        });
      } else {
        await updateTask(selectedData._id, {
          bodyData: JSON.stringify({ subTask }),
        });
      }
      setOpenDrawer(false);
      GetData(selectedData._id);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  return (
    <div className="addRenderingPopup">
      <Box className="addContainerHeader">
        <Typography className="headerTitle">
          {viewMode === ADD_MODE
            ? "Add Sub Task"
            : viewMode === EDIT_MODE
            ? "Edit Sub Task"
            : "View Sub Task"}
        </Typography>
        <CloseIcon
          className="Close-Icon"
          onClick={() => setOpenDrawer(false)}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />

      <Formik
        initialValues={{
          workType: initialSubTask.workType || "",
          status: initialSubTask.status || "",
          workDescription: initialSubTask.workDescription || "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubTaskSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" flexDirection="column" gap={2} mt={2}>
                <Autocomplete
                  disabled={isViewMode}
                  options={WORK_TYPE_OPTIONS}
                  value={values.workType}
                  onChange={(_, value) => setFieldValue("workType", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Work Type"
                      name="workType"
                      error={touched.workType && Boolean(errors.workType)}
                      helperText={touched.workType && errors.workType}
                      fullWidth
                    />
                  )}
                />

                <Autocomplete
                  disabled={isViewMode}
                  options={STATUS_OPTIONS}
                  value={values.status}
                  onChange={(_, value) => setFieldValue("status", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      name="status"
                      error={touched.status && Boolean(errors.status)}
                      helperText={touched.status && errors.status}
                      fullWidth
                    />
                  )}
                />

                {dateAndTime.map((entry, index) => (
                  <React.Fragment key={entry.id}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={3}>
                        <DatePicker
                          label="Date"
                          format="DD/MM/YYYY"
                          value={entry.date}
                          disabled={isViewMode}
                          onChange={(value) =>
                            handleDateTimeChange(entry.id, "date", value)
                          }
                          maxDate={dayjs()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={Boolean(timeErrors[entry.id])}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TimePicker
                          label="Start Time"
                          value={entry.time.startTime}
                          disabled={isViewMode}
                          onChange={(value) =>
                            handleDateTimeChange(entry.id, "startTime", value)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={Boolean(timeErrors[entry.id])}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TimePicker
                          label="End Time"
                          value={entry.time.endTime}
                          disabled={isViewMode}
                          onChange={(value) =>
                            handleDateTimeChange(entry.id, "endTime", value)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={Boolean(timeErrors[entry.id])}
                              helperText={timeErrors[entry.id]}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        {!isViewMode && (
                          <Stack direction="row" spacing={1}>
                            {dateAndTime.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveEntry(entry.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                            {index === dateAndTime.length - 1 && (
                              <IconButton onClick={handleAddEntry}>
                                <AddIcon />
                              </IconButton>
                            )}
                          </Stack>
                        )}
                      </Grid>
                    </Grid>
                    {timeErrors[entry.id] && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: -1, ml: 2 }}
                      >
                        {timeErrors[entry.id]}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}

                <TextField
                  label="Description"
                  name="workDescription"
                  multiline
                  rows={4}
                  disabled={isViewMode}
                  value={values.workDescription}
                  onChange={handleChange}
                  error={
                    touched.workDescription && Boolean(errors.workDescription)
                  }
                  helperText={touched.workDescription && errors.workDescription}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      minHeight: "100px !important",
                      height: "auto !important",
                    },
                  }}
                />

                {!isViewMode && (
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button type="submit" variant="contained" size="small">
                      {viewMode === ADD_MODE ? "Add Sub Task" : "Save Changes"}
                    </Button>
                  </Box>
                )}
              </Box>
            </LocalizationProvider>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddSubTask;
